import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  indexOfFirstItem: number;
  indexOfLastItem: number;
  onPageChange: (pageNumber: number) => void;
  onItemsPerPageChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  hasMore?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  indexOfFirstItem,
  indexOfLastItem,
  onPageChange,
  onItemsPerPageChange,
  hasMore = true
}) => {
  const { t } = useLanguage();
  const [isMobile, setIsMobile] = useState(false);

  // Check if on mobile for better pagination display
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  return (
    <>
      {totalItems > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-3 flex flex-col sm:flex-row justify-between items-center gap-y-3 gap-x-2 sm:gap-3 sm:mb-4 pb-4 sm:pb-2 relative z-10 my-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400 w-full sm:w-auto justify-center sm:justify-start">
            <span className="whitespace-nowrap">{t('common.pagination.showing')}</span>
            <select
              value={itemsPerPage}
              onChange={onItemsPerPageChange}
              className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-1 sm:px-2 py-0.5 sm:py-1 text-gray-800 dark:text-gray-200 text-xs sm:text-sm"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="whitespace-nowrap">{t('common.pagination.itemsPerPage')}</span>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 w-full sm:w-auto justify-center">
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
              {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, totalItems)} {t('common.pagination.of')} {totalItems}
            </div>

            <div className="flex">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="bg-gray-100 dark:bg-gray-700 p-1 sm:p-2 rounded-l-md border-r border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                <ChevronLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>

              {/* Page numbers - show current, first, last, and one before/after current */}
              <div className="flex">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page =>
                    page === 1 ||
                    page === totalPages ||
                    Math.abs(page - currentPage) <= (isMobile ? 0 : 1) // Show fewer pages on mobile
                  )
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span className="bg-gray-100 dark:bg-gray-700 px-2 sm:px-3 py-1 sm:py-2 border-r border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 text-xs sm:text-sm">...</span>
                      )}
                      <button
                        onClick={() => onPageChange(page)}
                        className={`px-2 sm:px-3 py-1 sm:py-2 border-r border-gray-300 dark:border-gray-600 text-xs sm:text-sm ${currentPage === page
                          ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))
                }
              </div>

              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={(currentPage === totalPages && !hasMore) || totalPages === 0}
                className="bg-gray-100 dark:bg-gray-700 p-1 sm:p-2 rounded-r-md disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Pagination;
